import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  Users,
  ClipboardCheck,
  TrendingUp,
  Clock,
  BookOpen,
  Plus,
  Calendar,
  Activity,
  Target,
  AlertCircle,
  ArrowUpRight,
  Layers,
  Loader2,
} from 'lucide-react';
import { Card, CardHeader, StatCard, Badge, Button } from '@azyntor-labs/nexus-ui';
import { dashboardApi } from '@/api/endpoints';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { formatExamDateShort } from '@/utils';
import { useExamAuth } from '@/hooks/useExamAuth';

// Loading skeleton for sections
const SectionSkeleton = () => (
  <div className="animate-pulse space-y-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
        <div className="h-4 bg-white/10 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-white/5 rounded w-1/2"></div>
      </div>
    ))}
  </div>
);

export const TeacherDashboard = () => {
  const { user } = useExamAuth();

  // Separate API calls for each section - allows parallel loading and independent caching
  const { data: examsResponse, isLoading: isLoadingExams } = useQuery({
    queryKey: ['teacher-exams'],
    queryFn: () => dashboardApi.getTeacherExams(),
    staleTime: 30000, // 30 seconds
  });

  const { data: scheduleResponse, isLoading: isLoadingSchedule } = useQuery({
    queryKey: ['teacher-today-schedule'],
    queryFn: () => dashboardApi.getTeacherTodaySchedule(),
    staleTime: 60000, // 1 minute
  });

  const { data: gradingResponse, isLoading: isLoadingGrading } = useQuery({
    queryKey: ['teacher-pending-grading'],
    queryFn: () => dashboardApi.getTeacherPendingGrading(),
    staleTime: 30000,
  });

  const { data: resultsResponse, isLoading: isLoadingResults } = useQuery({
    queryKey: ['teacher-recent-results'],
    queryFn: () => dashboardApi.getTeacherRecentResults(5),
    staleTime: 60000,
  });

  const { data: questionBanksResponse, isLoading: isLoadingQuestionBanks } = useQuery({
    queryKey: ['teacher-question-bank-stats'],
    queryFn: () => dashboardApi.getTeacherQuestionBankStats(),
    staleTime: 120000, // 2 minutes - question banks don't change often
  });

  const { data: alertsResponse, isLoading: isLoadingAlerts } = useQuery({
    queryKey: ['teacher-student-alerts'],
    queryFn: () => dashboardApi.getTeacherStudentAlerts(),
    staleTime: 30000,
  });

  // Extract data from responses
  const exams = Array.isArray(examsResponse?.data?.data) ? examsResponse.data.data : [];
  const todaySchedule = Array.isArray(scheduleResponse?.data?.data) ? scheduleResponse.data.data : [];
  const pendingGrading = Array.isArray(gradingResponse?.data?.data) ? gradingResponse.data.data : [];
  const recentResults = Array.isArray(resultsResponse?.data?.data) ? resultsResponse.data.data : [];
  const questionBankStats = Array.isArray(questionBanksResponse?.data?.data) ? questionBanksResponse.data.data : [];
  const studentAlerts = Array.isArray(alertsResponse?.data?.data) ? alertsResponse.data.data : [];

  // Check if critical data is loading (exams and question banks for stats)
  const isLoadingCritical = isLoadingExams || isLoadingQuestionBanks;

  const activeExams = exams.filter((e) => e.status === 'active').length;
  const scheduledExams = exams.filter((e) => e.status === 'scheduled').length;
  const draftExams = exams.filter((e) => e.status === 'draft').length;
  const completedExams = exams.filter((e) => e.status === 'completed').length;
  const totalQuestions = questionBankStats.reduce((sum, qb) => sum + qb.total_questions, 0);

  // Only show full loading state for critical data (exams stats)
  if (isLoadingCritical) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-400 mx-auto mb-4" />
          <p className="text-white/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Welcome, {user?.first_name}!</h1>
          <p className="text-white/50 mt-1">Manage your exams and track student performance</p>
        </div>
        <Link to="/teacher/exams/create">
          <Button leftIcon={<Plus className="w-4 h-4" />}>Create Exam</Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Exams"
          value={exams.length}
          icon={<FileText />}
          color="teal"
          subtitle={`${activeExams} active`}
        />
        <StatCard
          title="Active Exams"
          value={activeExams}
          icon={<Activity />}
          color="green"
        />
        <StatCard
          title="Pending Grading"
          value={pendingGrading.length}
          icon={<ClipboardCheck />}
          color="orange"
          subtitle="Needs review"
        />
        <StatCard
          title="Question Banks"
          value={questionBankStats.length}
          icon={<Layers />}
          color="teal"
          subtitle={`${totalQuestions} questions`}
        />
      </div>

      {/* Exam Status Distribution */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-white/60">Draft</span>
            <FileText className="w-4 h-4 text-white/40" />
          </div>
          <p className="text-2xl font-bold text-white">{draftExams}</p>
          <div className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-white/40 rounded-full" style={{ width: `${exams.length > 0 ? (draftExams / exams.length) * 100 : 0}%` }} />
          </div>
        </div>
        <div className="glass-card p-4 border-l-2 border-amber-500/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-amber-400/80">Scheduled</span>
            <Clock className="w-4 h-4 text-amber-400/60" />
          </div>
          <p className="text-2xl font-bold text-white">{scheduledExams}</p>
          <div className="mt-2 h-1 bg-amber-500/20 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${exams.length > 0 ? (scheduledExams / exams.length) * 100 : 0}%` }} />
          </div>
        </div>
        <div className="glass-card p-4 border-l-2 border-emerald-500/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-emerald-400/80">Active</span>
            <Activity className="w-4 h-4 text-emerald-400/60" />
          </div>
          <p className="text-2xl font-bold text-white">{activeExams}</p>
          <div className="mt-2 h-1 bg-emerald-500/20 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${exams.length > 0 ? (activeExams / exams.length) * 100 : 0}%` }} />
          </div>
        </div>
        <div className="glass-card p-4 border-l-2 border-blue-500/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-400/80">Completed</span>
            <Users className="w-4 h-4 text-blue-400/60" />
          </div>
          <p className="text-2xl font-bold text-white">{completedExams}</p>
          <div className="mt-2 h-1 bg-blue-500/20 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${exams.length > 0 ? (completedExams / exams.length) * 100 : 0}%` }} />
          </div>
        </div>
      </div>

      {/* Today's Schedule & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Schedule */}
        <Card>
          <CardHeader
            title="Today's Schedule"
            subtitle={isLoadingSchedule ? 'Loading...' : `${todaySchedule.length} exam${todaySchedule.length !== 1 ? 's' : ''} scheduled`}
          />
          {isLoadingSchedule ? (
            <SectionSkeleton />
          ) : todaySchedule.length > 0 ? (
            <div className="space-y-3">
              {todaySchedule.map((exam) => (
                <div
                  key={exam.exam_id}
                  className="p-4 rounded-lg bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-500/20"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{exam.title}</h3>
                      <p className="text-sm text-white/50">{exam.subject}</p>
                    </div>
                    <Badge variant="teal" size="sm">Today</Badge>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-white/50">
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {formatExamDateShort(exam.start_time)} - {formatExamDateShort(exam.end_time)}
                    </span>
                    <span className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {exam.enrolled_count} students
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {exam.duration_minutes} min
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40">No exams scheduled for today</p>
            </div>
          )}
        </Card>

        {/* Student Alerts */}
        <Card>
          <CardHeader
            title="Student Alerts"
            subtitle="Students needing attention"
            action={
              !isLoadingAlerts && studentAlerts.length > 0 && (
                <Badge variant="warning" size="sm">
                  {studentAlerts.length} alert{studentAlerts.length !== 1 ? 's' : ''}
                </Badge>
              )
            }
          />
          {isLoadingAlerts ? (
            <SectionSkeleton />
          ) : studentAlerts.length > 0 ? (
            <div className="space-y-3">
              {studentAlerts.slice(0, 5).map((alert, index) => (
                <div
                  key={`${alert.student_id}-${index}`}
                  className={`p-3 rounded-lg border ${
                    alert.alert_type === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                    alert.alert_type === 'warning' ? 'bg-amber-500/10 border-amber-500/30' :
                    'bg-white/5 border-white/[0.06]'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <AlertCircle className={`w-5 h-5 mt-0.5 ${
                      alert.alert_type === 'critical' ? 'text-red-400' :
                      alert.alert_type === 'warning' ? 'text-amber-400' :
                      'text-white/50'
                    }`} />
                    <div className="flex-1">
                      <h4 className="font-semibold text-white text-sm">{alert.student_name}</h4>
                      <p className="text-xs text-white/60 mt-0.5">{alert.message}</p>
                      <p className="text-xs text-white/40 mt-1">
                        {alert.exam_title} - {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40">No student alerts</p>
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Your Exams */}
        <Card>
          <CardHeader
            title="Your Exams"
            subtitle={isLoadingExams ? 'Loading...' : 'Recently created exams'}
            action={
              <Link to="/teacher/exams" className="text-sm text-teal-400 hover:text-teal-300 font-medium flex items-center">
                View All <ArrowUpRight className="w-3 h-3 ml-1" />
              </Link>
            }
          />
          {isLoadingExams ? (
            <SectionSkeleton />
          ) : exams.length > 0 ? (
            <div className="space-y-3">
              {exams.slice(0, 5).map((exam) => (
                <div
                  key={exam.exam_id}
                  className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.1] transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{exam.title}</h3>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-white/50">
                        <span className="flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {exam.total_enrolled} enrolled
                        </span>
                        <span className="flex items-center">
                          <Target className="w-4 h-4 mr-1" />
                          {exam.average_score?.toFixed(1) || 0}% avg
                        </span>
                        <span>{formatExamDateShort(exam.start_time)}</span>
                      </div>
                    </div>
                    <Badge
                      variant={
                        exam.status === 'active'
                          ? 'success'
                          : exam.status === 'scheduled'
                          ? 'warning'
                          : exam.status === 'draft'
                          ? 'secondary'
                          : 'info'
                      }
                      size="sm"
                    >
                      {exam.status}
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2 mt-3">
                    <Link to={`/teacher/exams/${exam.exam_id}/edit`}>
                      <Button variant="secondary" size="sm">Edit</Button>
                    </Link>
                    <Link to={`/analytics/exams/${exam.exam_id}`}>
                      <Button variant="ghost" size="sm">Analytics</Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40 mb-3">No exams created yet</p>
              <Link to="/teacher/exams/create">
                <Button size="sm">Create Your First Exam</Button>
              </Link>
            </div>
          )}
        </Card>

        {/* Pending Grading */}
        <Card>
          <CardHeader
            title="Pending Grading"
            subtitle={isLoadingGrading ? 'Loading...' : 'Exams awaiting your review'}
            action={
              <Link to="/teacher/grading" className="text-sm text-teal-400 hover:text-teal-300 font-medium flex items-center">
                View All <ArrowUpRight className="w-3 h-3 ml-1" />
              </Link>
            }
          />
          {isLoadingGrading ? (
            <SectionSkeleton />
          ) : pendingGrading.length > 0 ? (
            <div className="space-y-3">
              {pendingGrading.slice(0, 5).map((item) => (
                <div
                  key={item.attempt_id}
                  className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] transition-all"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{item.exam_title}</h3>
                      <p className="text-sm text-white/50">Student: {item.student_name}</p>
                      <div className="flex items-center space-x-3 mt-1 text-xs text-white/40">
                        <span>{item.questions_to_grade} questions to grade</span>
                        <span>Auto-score: {item.auto_score}/{item.max_score}</span>
                      </div>
                      <p className="text-xs text-white/40 mt-1">
                        Submitted {formatDistanceToNow(new Date(item.submitted_at), { addSuffix: true })}
                      </p>
                    </div>
                    <Badge variant="warning" size="sm">Pending</Badge>
                  </div>
                  <Link to={`/teacher/grading/${item.attempt_id}`}>
                    <Button variant="outline" size="sm" className="w-full mt-2">
                      Grade Now
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ClipboardCheck className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40">All caught up!</p>
              <p className="text-sm text-white/30 mt-1">No exams pending grading</p>
            </div>
          )}
        </Card>
      </div>

      {/* Recent Results & Question Banks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Results */}
        <Card>
          <CardHeader
            title="Recent Results"
            subtitle={isLoadingResults ? 'Loading...' : 'Latest exam performance'}
          />
          {isLoadingResults ? (
            <SectionSkeleton />
          ) : recentResults.length > 0 ? (
            <div className="space-y-3">
              {recentResults.slice(0, 5).map((result) => (
                <div
                  key={result.exam_id}
                  className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-white">{result.exam_title}</h3>
                    <Badge
                      variant={result.pass_rate >= 70 ? 'success' : result.pass_rate >= 50 ? 'warning' : 'danger'}
                      size="sm"
                    >
                      {result.pass_rate.toFixed(0)}% pass
                    </Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div>
                      <p className="text-white/40 text-xs">Attempts</p>
                      <p className="font-semibold text-white">{result.total_attempts}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Avg Score</p>
                      <p className="font-semibold text-white">{result.average_score.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Highest</p>
                      <p className="font-semibold text-emerald-400">{result.highest_score.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Lowest</p>
                      <p className="font-semibold text-red-400">{result.lowest_score.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <TrendingUp className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40">No exam results yet</p>
            </div>
          )}
        </Card>

        {/* Question Banks */}
        <Card>
          <CardHeader
            title="Question Banks"
            subtitle={isLoadingQuestionBanks ? 'Loading...' : `${totalQuestions} total questions`}
            action={
              <Link to="/teacher/question-banks" className="text-sm text-teal-400 hover:text-teal-300 font-medium flex items-center">
                Manage <ArrowUpRight className="w-3 h-3 ml-1" />
              </Link>
            }
          />
          {isLoadingQuestionBanks ? (
            <SectionSkeleton />
          ) : questionBankStats.length > 0 ? (
            <div className="space-y-3">
              {questionBankStats.slice(0, 5).map((qb) => (
                <div
                  key={qb.id}
                  className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-white">{qb.name}</h3>
                      <p className="text-sm text-white/50">{qb.subject}</p>
                    </div>
                    <Badge variant="cyan" size="sm">{qb.total_questions} Q</Badge>
                  </div>
                  <div className="flex items-center space-x-2 text-xs mt-3">
                    <span className="px-2 py-0.5 bg-emerald-500/15 text-emerald-400 rounded border border-emerald-500/30">
                      Easy: {qb.questions_by_difficulty?.easy || 0}
                    </span>
                    <span className="px-2 py-0.5 bg-amber-500/15 text-amber-400 rounded border border-amber-500/30">
                      Medium: {qb.questions_by_difficulty?.medium || 0}
                    </span>
                    <span className="px-2 py-0.5 bg-red-500/15 text-red-400 rounded border border-red-500/30">
                      Hard: {qb.questions_by_difficulty?.hard || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40 mb-3">No question banks yet</p>
              <Link to="/teacher/question-banks/create">
                <Button size="sm">Create Question Bank</Button>
              </Link>
            </div>
          )}
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader title="Quick Actions" subtitle="Common tasks for teachers" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Link
            to="/teacher/exams/create"
            className="flex items-center space-x-3 p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-teal-500/10 hover:border-teal-500/30 transition-all group"
          >
            <div className="w-10 h-10 bg-teal-500/15 rounded-lg flex items-center justify-center group-hover:bg-teal-500/25 transition-colors">
              <Plus className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <h3 className="font-medium text-white">Create Exam</h3>
              <p className="text-sm text-white/40">Build a new exam</p>
            </div>
          </Link>

          <Link
            to="/teacher/question-banks"
            className="flex items-center space-x-3 p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-emerald-500/10 hover:border-emerald-500/30 transition-all group"
          >
            <div className="w-10 h-10 bg-emerald-500/15 rounded-lg flex items-center justify-center group-hover:bg-emerald-500/25 transition-colors">
              <BookOpen className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="font-medium text-white">Question Banks</h3>
              <p className="text-sm text-white/40">{totalQuestions} questions</p>
            </div>
          </Link>

          <Link
            to="/teacher/grading"
            className="flex items-center space-x-3 p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-amber-500/10 hover:border-amber-500/30 transition-all group"
          >
            <div className="w-10 h-10 bg-amber-500/15 rounded-lg flex items-center justify-center group-hover:bg-amber-500/25 transition-colors">
              <ClipboardCheck className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="font-medium text-white">Grade Exams</h3>
              <p className="text-sm text-white/40">{pendingGrading.length} pending</p>
            </div>
          </Link>

          <Link
            to="/analytics"
            className="flex items-center space-x-3 p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-blue-500/10 hover:border-blue-500/30 transition-all group"
          >
            <div className="w-10 h-10 bg-blue-500/15 rounded-lg flex items-center justify-center group-hover:bg-blue-500/25 transition-colors">
              <TrendingUp className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-medium text-white">Analytics</h3>
              <p className="text-sm text-white/40">View reports</p>
            </div>
          </Link>
        </div>
      </Card>
    </div>
  );
};
